import React, { useState, useEffect, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Search, Pen, Rocket } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import {
  SiOpenai,
  SiAmazonwebservices,
  SiLangchain,
  SiAnthropic,
  SiGoogle,
  SiPrefect,
  SiGitlab,
  SiHuggingface,
  SiMeta,
} from "react-icons/si";

import Header from "../../components/Header/Header";
import LightPillar from "../../components/LightPillar/LightPillar";
import "./MainPage.css";

// -- Assets


const fadeUp = { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0 } };
const fadeLeft = { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, x: 0 } };
const fadeRight = { hidden: { opacity: 0, x: 50 }, visible: { opacity: 1, x: 0 } };

const VIDEOS = [
  { id: "hGGm0pXCxS4", title: "Zenit AI Lab – Demo" },
  { id: "eWgT0SnrO5U", title: "Casos de Uso con Zenit AI Lab" },
  { id: "iJn1OjloYLI", title: "¿Cómo generar flujos de trabajo con inteligencia artificial?" },
  { id: "yTtaODkS68Y", title: "¿Cómo Integrar un asistente a un flujo de trabajo?" },
  { id: "1cthCDBilbo", title: "¿Qué es un asistente o un equipo en Zenit AI Lab?" },
];

const PARTNER_BRANDS = [
  { name: "OpenAI", logo: SiOpenai },
  { name: "AWS", logo: SiAmazonwebservices },
  { name: "LangChain", logo: SiLangchain },
  { name: "Anthropic", logo: SiAnthropic },
  { name: "Google", logo: SiGoogle },
  { name: "Prefect", logo: SiPrefect },
  { name: "GitLab", logo: SiGitlab },
  { name: "Hugging Face", logo: SiHuggingface },
  { name: "Meta AI", logo: SiMeta },
];

gsap.registerPlugin(ScrollTrigger);

export default function MainPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const lenisRef = React.useRef(null);
  const servicesSectionRef = React.useRef(null);
  const repeatedBrands = [...PARTNER_BRANDS, ...PARTNER_BRANDS, ...PARTNER_BRANDS];
  const repeatedBrandsReversed = [...PARTNER_BRANDS].reverse();
  const repeatedBrandsBottom = [
    ...repeatedBrandsReversed,
    ...repeatedBrandsReversed,
    ...repeatedBrandsReversed,
  ];

  useLayoutEffect(() => {
    const lenis = new Lenis({
      duration: 1,
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.05,
    });

    lenisRef.current = lenis;

    const updateScrollTrigger = () => {
      ScrollTrigger.update();
    };

    const lenisTick = (time) => {
      lenis.raf(time * 1000);
    };

    lenis.on("scroll", updateScrollTrigger);
    gsap.ticker.add(lenisTick);
    gsap.ticker.lagSmoothing(0);

    const servicesContext = gsap.context(() => {
      gsap.set(".services-grid-energy-line", {
        transformOrigin: "0% 50%",
        scaleX: 0,
        opacity: 0.12,
      });

      gsap.fromTo(
        ".services-heading-group > *",
        {
          autoAlpha: 0,
          y: 52,
          rotateX: -22,
          filter: "blur(12px)",
          transformPerspective: 1000,
        },
        {
          autoAlpha: 1,
          y: 0,
          rotateX: 0,
          filter: "blur(0px)",
          duration: 1.05,
          ease: "power4.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: ".services-section",
            start: "top 72%",
          },
        }
      );

      gsap.fromTo(
        ".service-card",
        {
          autoAlpha: 0,
          y: 92,
          scale: 0.84,
          rotateX: 24,
          rotateY: (index) => (index % 2 === 0 ? -13 : 13),
          filter: "blur(8px)",
        },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          rotateX: 0,
          rotateY: 0,
          filter: "blur(0px)",
          duration: 1.08,
          ease: "power4.out",
          stagger: {
            each: 0.1,
            from: "start",
          },
          scrollTrigger: {
            trigger: ".services-grid",
            start: "top 78%",
          },
        }
      );

      gsap.fromTo(
        ".services-grid-energy-line--top",
        { scaleX: 0, opacity: 0.16 },
        {
          scaleX: 1,
          opacity: 0.74,
          ease: "none",
          scrollTrigger: {
            trigger: ".services-heading-group",
            start: "top 96%",
            end: "center 62%",
            scrub: 0.2,
            invalidateOnRefresh: true,
          },
        }
      );

      gsap.fromTo(
        ".services-grid-energy-line--mid",
        { scaleX: 0, opacity: 0.12 },
        {
          scaleX: 1,
          opacity: 0.52,
          ease: "none",
          scrollTrigger: {
            trigger: ".services-heading-group",
            start: "top 94%",
            end: "bottom 56%",
            scrub: 0.22,
            invalidateOnRefresh: true,
          },
        }
      );

      ScrollTrigger.refresh();

      gsap.to(".service-card-halo", {
        opacity: 0.78,
        scale: 1.06,
        duration: 2.8,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        stagger: 0.18,
      });

      gsap.to(".service-card-beam", {
        xPercent: 140,
        duration: 4.9,
        ease: "none",
        repeat: -1,
        stagger: 0.2,
      });

      gsap.to(".services-orb--a", {
        xPercent: 14,
        yPercent: -16,
        duration: 19,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });

      gsap.to(".services-orb--b", {
        xPercent: -13,
        yPercent: 17,
        duration: 21,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });

      gsap.to(".services-orb--a", {
        rotate: 18,
        scrollTrigger: {
          trigger: ".services-section",
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });

      gsap.to(".services-orb--b", {
        rotate: -16,
        scrollTrigger: {
          trigger: ".services-section",
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, servicesSectionRef);

    return () => {
      servicesContext.revert();
      gsap.ticker.remove(lenisTick);
      lenis.off("scroll", updateScrollTrigger);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    const sectionId = searchParams.get("scrollTo");
    if (sectionId) {
      const el = document.getElementById(sectionId);
      if (el) {
        if (lenisRef.current) {
          lenisRef.current.scrollTo(el, {
            duration: 1,
            offset: 0,
          });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }

      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const [activeVideoId, setActiveVideoId] = useState(VIDEOS[0].id);
  const activeIndex = VIDEOS.findIndex((v) => v.id === activeVideoId);
  const sidebarRef = React.useRef(null);

  const goToPrev = () => {
    sidebarRef.current?.scrollBy({ top: -160, behavior: "smooth" });
  };
  const goToNext = () => {
    sidebarRef.current?.scrollBy({ top: 160, behavior: "smooth" });
  };

  return (
    <div className="main-page">
      <div className="background-fixed"></div>
      <div className="main-page-content">
        <Header />

        {/* ─ Hero ─ */}
        <div className="main-body">
          <div className="hero-light-pillar-layer" aria-hidden="true">
            <LightPillar
              topColor="#B23CFF"
              bottomColor="#7A22CC"
              intensity={0.82}
              rotationSpeed={0.65}
              glowAmount={0.0016}
              pillarWidth={3}
              pillarHeight={0.4}
              noiseIntensity={0.28}
              pillarRotation={108}
              interactive={false}
              mixBlendMode="normal"
              quality="high"
              pauseWhenOffscreen={true}
              fps={30}
            />
          </div>

          <motion.div
            className="hero-main-content"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <h1>
              <span className="hero-title-line1">
                {t("mainPage.hero.titleStart")}{" "}
                <span className="hero-title-highlight">{t("mainPage.hero.titleCon")}</span>
              </span>
              <br />
              <span className="hero-title-highlight">{t("mainPage.hero.titleHighlight")}</span>
            </h1>
            <p className="hero-description">{t("mainPage.hero.description")}</p>
            <div className="hero-buttons">
              <a href="/Contact" className="hero-btn-primary">{t("mainPage.hero.ctaPrimary")}</a>
              <a href="#ai-lab" className="hero-btn-secondary">{t("mainPage.hero.ctaSecondary")}</a>
            </div>
          </motion.div>

          <section className="partners-band-section hero-partners-bottom" aria-label="Tecnologias">
            <p className="partners-band-powered">Powered by</p>

            <div className="partners-band-marquee partners-band-marquee--top">
              <div className="partners-band-track">
                {repeatedBrands.map((brand, index) => (
                  <span key={`top-${brand.name}-${index}`} className="partners-band-item">
                    <brand.logo className="partners-band-logo" aria-hidden="true" />
                    <span>{brand.name}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="partners-band-marquee partners-band-marquee--bottom">
              <div className="partners-band-track">
                {repeatedBrandsBottom.map((brand, index) => (
                  <span key={`bottom-${brand.name}-${index}`} className="partners-band-item">
                    <brand.logo className="partners-band-logo" aria-hidden="true" />
                    <span>{brand.name}</span>
                  </span>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* ─ Services Section ─ */}
        <section
          id="ai-lab"
          className="services-section"
          ref={servicesSectionRef}
        >
          <div className="services-ambient" aria-hidden="true">
            <span className="services-orb services-orb--a"></span>
            <span className="services-orb services-orb--b"></span>
            <span className="services-grid-energy-line services-grid-energy-line--top"></span>
            <span className="services-grid-energy-line services-grid-energy-line--mid"></span>
          </div>

          <div className="services-heading-group">
            <p className="services-subtitle">
              {t("servicesSection.subtitle")}
            </p>

            <h2 className="services-title">
              {t("servicesSection.title")}
            </h2>

            <p className="services-description">
              {t("servicesSection.description")}
            </p>
          </div>

          <div className="services-grid">
            {/* Service 1: AI Agents */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M24 28C25.6569 28 27 26.6569 27 25C27 23.3431 25.6569 22 24 22C22.3431 22 21 23.3431 21 25C21 26.6569 22.3431 28 24 28Z" fill="currentColor" />
                  <path d="M40 28C41.6569 28 43 26.6569 43 25C43 23.3431 41.6569 22 40 22C38.3431 22 37 23.3431 37 25C37 26.6569 38.3431 28 40 28Z" fill="currentColor" />
                  <path d="M22 38C22 38 26 42 32 42C38 42 42 38 42 38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.aiAgents.title")}</h3>
              <p className="service-description">{t("servicesSection.services.aiAgents.description")}</p>
            </article>

            {/* Service 2: Custom Chatbots */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="12" y="16" width="40" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 26H44M20 34H38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M32 48L28 52H36L32 48Z" fill="currentColor" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.customChatbots.title")}</h3>
              <p className="service-description">{t("servicesSection.services.customChatbots.description")}</p>
            </article>

            {/* Service 3: Automated Workflows */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="32" r="6" stroke="currentColor" strokeWidth="2" />
                  <circle cx="48" cy="20" r="6" stroke="currentColor" strokeWidth="2" />
                  <circle cx="48" cy="44" r="6" stroke="currentColor" strokeWidth="2" />
                  <path d="M22 32H32M32 32V20M32 20H42M32 32V44M32 44H42" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.automatedWorkflows.title")}</h3>
              <p className="service-description">{t("servicesSection.services.automatedWorkflows.description")}</p>
            </article>

            {/* Service 4: Data Integration */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="14" y="14" width="16" height="16" stroke="currentColor" strokeWidth="2" />
                  <rect x="34" y="14" width="16" height="16" stroke="currentColor" strokeWidth="2" />
                  <rect x="14" y="34" width="16" height="16" stroke="currentColor" strokeWidth="2" />
                  <rect x="34" y="34" width="16" height="16" stroke="currentColor" strokeWidth="2" />
                  <path d="M30 22H34M22 30V34M42 30V34M30 42H34" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.dataIntegration.title")}</h3>
              <p className="service-description">{t("servicesSection.services.dataIntegration.description")}</p>
            </article>

            {/* Service 5: Process Automation */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="18" stroke="currentColor" strokeWidth="2" />
                  <path d="M32 20V32L40 36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M32 8V14M32 50V56M8 32H14M50 32H56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.processAutomation.title")}</h3>
              <p className="service-description">{t("servicesSection.services.processAutomation.description")}</p>
            </article>

            {/* Service 6: Analytics & Reports */}
            <article className="service-card">
              <span className="service-card-halo" aria-hidden="true"></span>
              <span className="service-card-beam" aria-hidden="true"></span>
              <div className="service-icon">
                <svg className="icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="12" y="12" width="40" height="40" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 38L28 30L34 36L44 26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="44" cy="26" r="2" fill="currentColor" />
                </svg>
              </div>
              <h3 className="service-title">{t("servicesSection.services.analyticsReports.title")}</h3>
              <p className="service-description">{t("servicesSection.services.analyticsReports.description")}</p>
            </article>
          </div>
        </section>

        {/* ─ Process Section - How We Work ─ */}
        <section className="process-section">
          <motion.p
            className="process-subtitle"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            {t("processSection.subtitle")}
          </motion.p>

          <motion.h2
            className="process-title"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            {t("processSection.title")} <span className="title-highlight">{t("processSection.titleHighlight")}</span>
          </motion.h2>

          <div className="process-steps">
            {/* Step 1 */}
            <motion.div
              className="process-step"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="step-icon-wrapper">
                <div className="step-icon">
                  <Search size={56} strokeWidth={2.5} />
                </div>
              </div>
              <p className="step-label">{t("processSection.steps.step1.label")}</p>
              <h3 className="step-title">{t("processSection.steps.step1.title")}</h3>
              <p className="step-description">{t("processSection.steps.step1.description")}</p>
            </motion.div>

            {/* Connecting Line 1 */}
            <div className="step-connector"></div>

            {/* Step 2 */}
            <motion.div
              className="process-step"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="step-icon-wrapper">
                <div className="step-icon">
                  <Pen size={56} strokeWidth={2.5} />
                </div>
              </div>
              <p className="step-label">{t("processSection.steps.step2.label")}</p>
              <h3 className="step-title">{t("processSection.steps.step2.title")}</h3>
              <p className="step-description">{t("processSection.steps.step2.description")}</p>
            </motion.div>

            {/* Connecting Line 2 */}
            <div className="step-connector"></div>

            {/* Step 3 */}
            <motion.div
              className="process-step"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="step-icon-wrapper">
                <div className="step-icon">
                  <Rocket size={56} strokeWidth={2.5} />
                </div>
              </div>
              <p className="step-label">{t("processSection.steps.step3.label")}</p>
              <h3 className="step-title">{t("processSection.steps.step3.title")}</h3>
              <p className="step-description">{t("processSection.steps.step3.description")}</p>
            </motion.div>
          </div>
        </section>

        {/* ─ Video Gallery ─ */}
        <section className="video-gallery-section">
          <motion.div
            className="video-gallery-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="video-gallery-title">{t("mainPage.videos.title")}</h2>
            <p className="video-gallery-subtitle">{t("mainPage.videos.subtitle")}</p>
          </motion.div>

          <motion.div
            className="video-gallery-container"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.15 }}
            transition={{ duration: 0.7 }}
          >
            <motion.div
              className="video-gallery-main"
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <div className="video-device">
                <div className="video-device-screen">
                  <div className="video-device-topbar">
                    <span className="video-device-camera" />
                  </div>
                  <div className="video-device-display">
                    <iframe
                      key={activeVideoId}
                      className="video-gallery-iframe"
                      src={`https://www.youtube.com/embed/${activeVideoId}?rel=0`}
                      title="Zenit AI Lab"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
                <div className="video-device-base">
                  <div className="video-device-neck" />
                  <div className="video-device-foot" />
                </div>
              </div>
            </motion.div>
            <motion.div
              className="video-gallery-sidebar-wrapper"
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <button
                className="video-gallery-nav-btn"
                onClick={goToPrev}
                disabled={activeIndex === 0}
                aria-label="Video anterior"
              >
                &#9650;
              </button>
              <div className="video-gallery-sidebar" ref={sidebarRef}>
                {VIDEOS.map((video, index) => (
                  <motion.button
                    key={video.id}
                    className={`video-gallery-item${activeVideoId === video.id ? " active" : ""}`}
                    onClick={() => setActiveVideoId(video.id)}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false, amount: 0.1 }}
                    transition={{ duration: 0.4, delay: 0.35 + index * 0.08 }}
                  >
                    <div className="video-gallery-thumb-wrap">
                      <img
                        src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                        alt={video.title}
                        className="video-gallery-thumb"
                      />
                      <span className="video-gallery-play-icon">&#9654;</span>
                    </div>
                    <span className="video-gallery-item-title">{video.title}</span>
                  </motion.button>
                ))}
              </div>
              <button
                className="video-gallery-nav-btn"
                onClick={goToNext}
                disabled={activeIndex === VIDEOS.length - 1}
                aria-label="Video siguiente"
              >
                &#9660;
              </button>
            </motion.div>
          </motion.div>
        </section>

      </div>
    </div>
  );
}